<template>
  <div class="cod-success">
    <van-nav-bar :fixed="true" z-index="9" :title="$t('orderPage.codPaymentReminder')" :border="false" left-arrow @click-left="goHome" />
    <template v-if="orderDetail">
      <div class="cod-success__icon">
        <van-icon name="checked" />
      </div>
      <h1>{{ $t('orderPage.codSuccessTitle') }}</h1>
      <p class="cod-success__desc">{{ $t('orderPage.codSuccessDesc') }}</p>

      <div class="cod-success__card">
        <div class="card-title">{{ $t('orderPage.rincianPesanan') }}</div>
        <div class="detail-row">
          <span>{{ $t('orderPage.orderNumber') }}</span>
          <span>{{ orderDetail.orderId }}</span>
        </div>
        <div class="detail-row">
          <span>{{ $t('orderPage.payMethodTitle') }}</span>
          <span>Cash on Delivery (COD)</span>
        </div>
        <div class="detail-row">
          <span>{{ $t('orderPage.securityDeposit') }}</span>
          <span>{{ priceUnit }}{{ orderDetail.depositAmount | numberFormat }}</span>
        </div>
        <div class="detail-row">
          <span>{{ $t('orderPage.firstRentalAmount') }}</span>
          <span>{{ priceUnit }}{{ initialRentalPayment | numberFormat }}</span>
        </div>
        <div class="detail-row">
          <span>{{ $t('orderPage.codShippingFee') }}</span>
          <span>{{ priceUnit }}{{ codShippingFeeAmount | numberFormat }}</span>
        </div>
        <div class="detail-divider"></div>
        <div class="detail-row total">
          <span>{{ $t('orderPage.dueAfterApproval') }}</span>
          <span>{{ priceUnit }}{{ dueAfterApprovalAmount | numberFormat }}</span>
        </div>
        <div class="detail-row total">
          <span>{{ $t('orderPage.dueOnDelivery') }}</span>
          <span>{{ priceUnit }}{{ dueOnDeliveryAmount | numberFormat }}</span>
        </div>
      </div>

      <div class="cod-success__notices">
        <div class="notice-card">
          <div class="notice-icon"><van-icon name="info" /></div>
          <div class="notice-content">
            <div class="notice-title">{{ $t('orderPage.codSuccessCardTitle') }}</div>
            <div class="notice-desc notice-desc--delivery">
              <span class="notice-currency">{{ priceUnit }}</span>
              <span class="notice-delivery-copy">
                {{ $t('orderPage.codDeliveryReminderBefore') }}
                <strong>{{ dueOnDeliveryAmount | numberFormat }}</strong>
                {{ $t('orderPage.codDeliveryReminderAfter') }}
              </span>
            </div>
          </div>
        </div>
        <div class="notice-card">
          <div class="notice-icon"><van-icon name="info" /></div>
          <div class="notice-content">
            <div class="notice-title">{{ $t('orderPage.dueOnApproval') }}</div>
            <div class="notice-desc">{{ $t('orderPage.codSuccessShippingFeeNoticeDesc') }}</div>
          </div>
        </div>
      </div>

      <div class="cod-success__actions">
        <div class="btn-primary" @click="goHome">{{ $t('orderPage.backToHome') }}</div>
        <div class="btn-secondary" @click="viewOrder">{{ $t('orderPage.viewOrder') }}</div>
      </div>
    </template>
    <div v-else class="cod-success__empty">
      <div>{{ loadFailed ? $t('systemError') : $t('loading') }}</div>
      <div v-if="loadFailed" class="btn-primary retry-btn" @click="loadOrderDetail">{{ $t('confirm') }}</div>
    </div>
    <WithholdingAuthPopup v-model="showWithholdingAuthPopup" @authorize="onWithholdingAuthAuthorize" />
  </div>
</template>

<script>
import { Icon, Toast } from 'vant';
import { queryOrderDetail } from '@/api/order';
import { applyDirectDebit } from '@/api/payment';
import { getAppSystemConfig } from '@/api/common';
import { ORDER_STATUS } from '@/common/constants';
import WithholdingAuthPopup from '@/components/WithholdingAuthPopup/index.vue';
import { hasWithholdingAuthShown, markWithholdingAuthShown } from '@/common/withholdingAuth';
import { DEFAULT_REG_CHANNEL } from '@/config';

export default {
  name: 'CodSuccess',
  components: {
    'van-icon': Icon,
    WithholdingAuthPopup
  },
  data() {
    return {
      orderId: '',
      orderDetail: null,
      loadFailed: false,
      showWithholdingAuthPopup: false,
      directDebitAuthorizing: false,
      memberInfoFetched: false,
      autoDebitEnabled: false,
      autoDebitConfigPromise: null
    }
  },
  computed: {
    priceUnit() {
      return this.$t('priceUnit');
    },
    initialRentalPayment() {
      const calculated = [this.orderDetail?.planPrice, this.orderDetail?.riskAmount]
        .reduce((total, amount) => total + Math.round((Number(amount) || 0) * 100), 0) / 100;
      return calculated || (this.orderDetail?.firstPayAmount || 0) - (this.orderDetail?.depositAmount || 0);
    },
    codShippingFeeAmount() {
      return Number(
        this.orderDetail?.codShippingFee ||
        this.orderDetail?.goodsInfo?.codShippingFee ||
        this.orderDetail?.goods?.codShippingFee ||
        0
      );
    },
    dueAfterApprovalAmount() {
      return this.codShippingFeeAmount;
    },
    dueOnDeliveryAmount() {
      return this.addMoney(this.orderDetail?.depositAmount, this.initialRentalPayment);
    }
  },
  mounted() {
    this.orderId = this.$route.query.orderId || '';
    if (!this.orderId) {
      this.$router.replace('/index');
      return;
    }
    this.loadOrderDetail();
  },
  methods: {
    addMoney(...amounts) {
      return amounts.reduce((total, amount) => total + Math.round((Number(amount) || 0) * 100), 0) / 100;
    },
    async onWithholdingAuthAuthorize() {
      if (this.directDebitAuthorizing) return;
      this.directDebitAuthorizing = true;
      try {
        const res = await applyDirectDebit({ route: this.$route });
        if (res && res.code === 'Success' && res.data && res.data.authUrl) {
          this.showWithholdingAuthPopup = false;
          window.location.href = res.data.authUrl;
          return;
        }
        Toast({ message: this.$t('systemError'), duration: 3000 });
      } catch (e) {
        Toast({ message: this.$t('systemError'), duration: 3000 });
      } finally {
        this.directDebitAuthorizing = false;
      }
    },
    loadAutoDebitConfig() {
      if (this.autoDebitConfigPromise) return this.autoDebitConfigPromise;
      this.autoDebitConfigPromise = getAppSystemConfig({
        platform: 'h5',
        channel: this.$store.state.user.channel || DEFAULT_REG_CHANNEL
      })
        .then((res) => {
          if (res?.code !== 'Success') return false;
          const features = res.data?.features || res.data || {};
          this.autoDebitEnabled = [true, 'true', 1, '1'].includes(features.autoDebit);
          return this.autoDebitEnabled;
        })
        .catch(() => false);
      return this.autoDebitConfigPromise;
    },
    async maybeShowWithholdingAuthPopup() {
      if (!this.orderId) return;
      if (!(await this.loadAutoDebitConfig())) return;
      const status = String(this.orderDetail?.status || '').trim().toUpperCase();
      if ([ORDER_STATUS.CLOSED, ORDER_STATUS.CANCELLED].includes(status)) return;
      if (hasWithholdingAuthShown(this.orderId)) return;
      // 先检查授权状态；若当前未授权，先进行一次查询再进入弹窗流程
      if (!this.$store.state.user.userInfo.hasWithholdingAuthorization) {
        if (!this.memberInfoFetched) {
          try {
            const res = await this.$store.dispatch('user/fetchMemberInfo', { _showLoading: false });
            if (!res || res.code !== 'Success') return;
            this.memberInfoFetched = true;
          } catch (e) {
            return;
          }
        }
      }
      if (this.$store.state.user.userInfo.hasWithholdingAuthorization) return;
      markWithholdingAuthShown(this.orderId);
      this.showWithholdingAuthPopup = true;
    },
    loadOrderDetail() {
      this.loadFailed = false;
      queryOrderDetail({ orderId: this.orderId }).then(res => {
        if (res?.code === 'Success') {
          this.orderDetail = res.data;
          this.maybeShowWithholdingAuthPopup();
        } else {
          this.loadFailed = true;
        }
      }).catch(() => {
        this.loadFailed = true;
      });
    },
    goHome() {
      this.$router.replace('/index');
    },
    viewOrder() {
      this.$router.replace(`/order/detail?orderId=${this.orderId}`);
    }
  }
}
</script>

<style lang="less" scoped>
.cod-success {
  min-height: 100vh;
  padding: 120px 30px 50px;
  box-sizing: border-box;
  background: #F6FBFF;
  text-align: center;

  ::v-deep .van-nav-bar {
    height: 108px;
    line-height: 108px;
    background: #FFFFFF;
  }

  ::v-deep .van-nav-bar__title {
    font-size: 36px;
    font-weight: 700;
    color: #0B0B12;
  }

  ::v-deep .van-nav-bar .van-icon {
    font-size: 42px;
    color: #0B0B12;
  }

  &__icon {
    margin: 36px auto 24px;
    width: 128px;
    height: 128px;
    border-radius: 64px;
    background: #EAF8EF;
    color: #16A34A;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 76px;
  }

  h1 {
    margin: 0;
    font-size: 38px;
    line-height: 48px;
    color: var(--text-color-black);
    font-weight: 700;
  }

  &__desc {
    margin: 18px 20px 0;
    font-size: 26px;
    line-height: 38px;
    color: var(--text-color-gray);
  }

  &__card {
    margin-top: 30px;
    padding: 34px;
    background: #FFFFFF;
    border-radius: 18px;
    text-align: left;
  }

  &__notices {
    margin-top: 40px;
    text-align: left;
  }

  &__actions {
    margin-top: 36px;

    .btn-primary,
    .btn-secondary {
      height: 88px;
      border-radius: 14px;
      font-size: 30px;
      line-height: 86px;
      text-align: center;
      font-weight: 500;
    }

    .btn-primary {
      background: var(--primary-color);
      color: #FFFFFF;
      border: none;
    }

    .btn-secondary {
      margin-top: 18px;
      border: 2px solid #D1D5DB;
      color: #FF7A00;
      background: #FFFFFF;
    }
  }

  &__empty {
    padding-top: 220px;
    font-size: 28px;
    color: var(--text-color-gray);
  }
}

.card-title,
.notice-title {
  font-size: 34px;
  line-height: 44px;
  font-weight: 600;
}

.card-title {
  color: #0B0B12;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  gap: 28px;
  margin-top: 24px;
  font-size: 28px;
  line-height: 38px;
  color: var(--text-color-gray);

  span:last-child {
    color: #111827;
    text-align: right;
  }

  &.total {
    font-weight: 600;
    color: #70758C;

    span:last-child {
      color: #0B45FF;
    }
  }
}

.detail-divider {
  margin: 26px 0 20px;
  border-top: 1px solid var(--border-color-base);
}

.notice-card {
  display: flex;
  gap: 26px;
  padding: 28px 34px;
  border: 1px solid #F3DFA8;
  border-radius: 18px;
  background: #FFF9E9;
  box-sizing: border-box;

  & + & {
    margin-top: 28px;
  }
}

.notice-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 64px;
  height: 64px;
  margin-top: 4px;
  border-radius: 16px;
  background: #FFFFFF;
  color: #FF9800;
  font-size: 36px;
}

.notice-content {
  flex: 1;
  min-width: 0;
}

.notice-title {
  color: #F59E0B;
  font-size: 34px;
  line-height: 44px;
  font-weight: 700;
}

.notice-desc {
  margin-top: 16px;
  color: #70758C;
  font-size: 28px;
  line-height: 36px;
}

.notice-desc--delivery {
  display: flex;
  align-items: flex-start;
  color: #70758C;
}

.notice-delivery-copy {
  flex: 1;
  min-width: 0;

  strong {
    display: inline-block;
    margin: 0 6px;
    color: #2F6BFF;
    font-weight: 500;
  }
}

.notice-currency {
  flex: 0 0 auto;
  margin-right: 8px;
  color: #2F6BFF;
  font-size: 40px;
  line-height: 40px;
}

.retry-btn {
  margin-top: 30px;
}
</style>
